import { useState } from 'react';

import { Button } from '@/components/ui/button';

import { TemplateCodeEditor } from '../TemplateCodeEditor';

import { AgentConfig } from './AgentConfig';
import { BookingConfig } from './BookingConfig';
import { CallApiConfig } from './CallApiConfig';
import { CampaignConfig } from './CampaignConfig';
import { CollectConfig } from './CollectConfig';
import { ConditionConfig } from './ConditionConfig';
import { CreatePaymentConfig } from './CreatePaymentConfig';
import { EnrichDataConfig } from './EnrichDataConfig';
import { FunctionConfig } from './FunctionConfig';
import { GetAnalyticsConfig } from './GetAnalyticsConfig';
import { GetQualityIssuesConfig } from './GetQualityIssuesConfig';
import { HandoffConfig } from './HandoffConfig';
import { NotificationConfig } from './NotificationConfig';
import { OutboundCallConfig } from './OutboundCallConfig';
import { RunClassifierConfig } from './RunClassifierConfig';
import { RunQualityConfig } from './RunQualityConfig';
import { RunReviewConfig } from './RunReviewConfig';
import { ScheduleWorkflowConfig } from './ScheduleWorkflowConfig';
import { SendEmailConfig } from './SendEmailConfig';
import { SendSmsConfig } from './SendSmsConfig';
import { StockConfig } from './StockConfig';
import { TrackingLinkConfig } from './TrackingLinkConfig';
import { UpdateCallConfig } from './UpdateCallConfig';
import { UpdateContactConfig } from './UpdateContactConfig';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type {
  BookingNodeData,
  CallApiNodeData,
  CampaignNodeData,
  CollectNodeData,
  ConditionNodeData,
  CreatePaymentNodeData,
  AgentNodeData,
  EnrichDataNodeData,
  FunctionNodeData,
  HandoffNodeData,
  NotificationNodeData,
  OutboundCallNodeData,
  RunClassifierNodeData,
  StockNodeData,
  TrackingLinkNodeData,
  RunQualityNodeData,
  RunReviewNodeData,
  ScheduleWorkflowNodeData,
  SendEmailNodeData,
  SendSmsNodeData,
  UpdateCallNodeData,
  UpdateContactNodeData,
  WorkflowNode,
} from '@repo/shared-types';

interface NodeConfigPanelProps {
  selectedNode: WorkflowNode | null;
  onNodeDataChange: (nodeId: string, data: Record<string, unknown>) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
  /** Every collect node on the canvas — a function preview runs them for its live input. */
  collectNodeDatas?: CollectNodeData[];
}

export const NodeConfigPanel = ({
  selectedNode,
  onNodeDataChange,
  collectNodeDatas,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: NodeConfigPanelProps) => {
  const [returnEditorOpen, setReturnEditorOpen] = useState(false);

  if (!selectedNode) {
    return null;
  }

  const handleChange = (data: Record<string, unknown>) => {
    onNodeDataChange(selectedNode.id, data);
  };

  const renderConfig = () => {
    switch (selectedNode.type) {
      case 'condition':
        return (
          <ConditionConfig
            data={selectedNode.data as unknown as ConditionNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'send_email':
        return (
          <SendEmailConfig
            data={selectedNode.data as unknown as SendEmailNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'send_sms':
        return (
          <SendSmsConfig
            data={selectedNode.data as unknown as SendSmsNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'update_call':
        return (
          <UpdateCallConfig
            data={selectedNode.data as unknown as UpdateCallNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'update_contact':
        return (
          <UpdateContactConfig
            data={selectedNode.data as unknown as UpdateContactNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'enrich_data':
        return (
          <EnrichDataConfig
            data={selectedNode.data as unknown as EnrichDataNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'run_review':
        return (
          <RunReviewConfig
            data={selectedNode.data as unknown as RunReviewNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'agent':
        return (
          <AgentConfig
            // AgentConfig holds the schema field list in local state (an unnamed
            // new field cannot survive a round-trip through JSON Schema), so it
            // must remount when a different node is selected.
            key={selectedNode.id}
            data={selectedNode.data as unknown as AgentNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'run_classifier':
        return (
          <RunClassifierConfig
            data={selectedNode.data as unknown as RunClassifierNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'run_quality':
        return (
          <RunQualityConfig
            data={selectedNode.data as unknown as RunQualityNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'call_api':
        return (
          <CallApiConfig
            data={selectedNode.data as unknown as CallApiNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'booking':
        return (
          <BookingConfig
            data={selectedNode.data as unknown as BookingNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'outbound_call':
        return (
          <OutboundCallConfig
            data={selectedNode.data as unknown as OutboundCallNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'notification':
        return (
          <NotificationConfig
            data={selectedNode.data as unknown as NotificationNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        );
      case 'stock':
        return (
          <StockConfig
            data={selectedNode.data as unknown as StockNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'collect':
        return (
          <CollectConfig
            data={selectedNode.data as unknown as CollectNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'campaign':
        return (
          <CampaignConfig
            data={selectedNode.data as unknown as CampaignNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'handoff':
        return (
          <HandoffConfig
            data={selectedNode.data as unknown as HandoffNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'schedule_workflow':
        return (
          <ScheduleWorkflowConfig
            data={selectedNode.data as unknown as ScheduleWorkflowNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
          />
        );
      case 'function':
        return (
          <FunctionConfig
            data={selectedNode.data as unknown as FunctionNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
            collectNodeDatas={collectNodeDatas}
          />
        );
      case 'create_payment':
        return (
          <CreatePaymentConfig
            data={selectedNode.data as unknown as CreatePaymentNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'tracking_link':
        return (
          <TrackingLinkConfig
            data={selectedNode.data as unknown as TrackingLinkNodeData}
            onChange={(d) => handleChange(d as unknown as Record<string, unknown>)}
          />
        );
      case 'get_analytics':
        return <GetAnalyticsConfig node={selectedNode} onDataChange={onNodeDataChange} />;
      case 'get_quality_issues':
        return <GetQualityIssuesConfig node={selectedNode} onDataChange={onNodeDataChange} />;
      case 'return': {
        const bodyStr =
          typeof selectedNode.data.body === 'string'
            ? selectedNode.data.body
            : JSON.stringify(selectedNode.data.body ?? {}, null, 2);
        return (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Response Body (JSON)</label>
              <p className="text-muted-foreground mb-1 text-xs">
                The JSON returned to the webhook caller. Supports template variables like{' '}
                {'{{webhook.body.field}}'} or {'{{nodes.<id>.status}}'}.
              </p>
              <div className="mb-1 flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReturnEditorOpen(true)}
                  className="h-6 text-[10px]"
                >
                  Open Editor
                </Button>
              </div>
              {bodyStr ? (
                <div
                  className="bg-muted/30 max-h-28 cursor-pointer overflow-hidden rounded-md border p-2"
                  onClick={() => setReturnEditorOpen(true)}
                >
                  <pre className="line-clamp-6 font-mono text-[10px] leading-relaxed text-gray-500">
                    {bodyStr}
                  </pre>
                </div>
              ) : (
                <p className="text-[10px] text-gray-500">
                  No body yet — click Open Editor to write.
                </p>
              )}
              <TemplateCodeEditor
                value={bodyStr}
                onChange={(v) => {
                  let parsed: unknown;
                  try {
                    parsed = JSON.parse(v);
                  } catch {
                    parsed = v;
                  }
                  handleChange({
                    ...selectedNode.data,
                    body: parsed,
                  } as Record<string, unknown>);
                }}
                type="json"
                open={returnEditorOpen}
                onOpenChange={setReturnEditorOpen}
              />
            </div>
          </div>
        );
      }
      case 'start':
        return <p className="text-muted-foreground text-sm">Start node has no configuration.</p>;
      default:
        return <p className="text-muted-foreground text-sm">Unknown node type.</p>;
    }
  };

  return (
    <aside className="bg-background flex w-[30%] min-w-[360px] flex-col border-l">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold capitalize">
          {selectedNode.type.replace(/_/g, ' ')} Node
        </h3>
        <p className="text-muted-foreground text-xs">ID: {selectedNode.id}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{renderConfig()}</div>
    </aside>
  );
};
